// appleMapKit.js - Apple MapKit JS loader, initialization and shared helpers.
// Replaces the Google Maps JS API for all map RENDERING.
// (Google Places autocomplete + geocoding are intentionally kept on Google.)

const MAPKIT_SRC = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js';

let mapkitPromise = null;

/**
 * Fetch a fresh MapKit JWT from our backend.
 * The token is server-minted so it works from any origin
 * (travy.buzz, localhost, capacitor://localhost).
 */
async function fetchMapKitToken() {
  const base = process.env.REACT_APP_API_URL || '/api';

  // The token lives 30 minutes, so a cached response is worse than useless —
  // Apple rejects an expired JWT with HTTP 401 on every tile and the map renders
  // as a blank grid. The unique query param matters as much as `no-store`: it
  // makes the request miss the Cache API entry that older service workers
  // created for this endpoint, so the map recovers on the very next load instead
  // of waiting for the new worker to activate.
  const res = await fetch(`${base}/mapkit/token?t=${Date.now()}`, {
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`MapKit token request failed: ${res.status}`);
  const data = await res.json();
  if (!data.token) throw new Error('MapKit token missing in response');
  return data.token;
}

/**
 * Load mapkit.js (once) and initialize it (once).
 * Returns a promise resolving to the global `mapkit` object.
 */
export function loadMapKit() {
  if (mapkitPromise) return mapkitPromise;

  mapkitPromise = new Promise((resolve, reject) => {
    const initMapKit = () => {
      try {
        window.mapkit.init({
          authorizationCallback: (done) => {
            fetchMapKitToken()
              .then(done)
              .catch((err) => {
                console.error('[MapKit] token fetch failed:', err);
                // Passing an invalid token surfaces a clear mapkit error
                done('');
              });
          },
          language: undefined, // let MapKit follow the browser/app language
        });

        // Surface authorization/tile failures. A structurally valid but
        // Apple-rejected token (wrong Key ID / private key mismatch, MapKit
        // capability not enabled for the key, or a domain-restricted Maps ID)
        // renders the map as a blank grid while custom DOM annotations still
        // show. Without this listener that failure is invisible in the console.
        if (typeof window.mapkit.addEventListener === 'function') {
          window.mapkit.addEventListener('error', (event) => {
            console.error(
              '[MapKit] authorization/tile error — the map tiles will not load. ' +
                'This almost always means Apple rejected the JWT (HTTP 401). ' +
                'Verify APPLE_MAPKIT_KEY_ID matches the .p8 in APPLE_MAPKIT_PRIVATE_KEY, ' +
                'that APPLE_TEAM_ID owns that key, and that the key has the MapKit JS ' +
                'capability enabled in the Apple Developer portal.',
              event
            );
          });
        }

        resolve(window.mapkit);
      } catch (err) {
        reject(err);
      }
    };

    if (window.mapkit && window.mapkit.init) {
      initMapKit();
      return;
    }

    const existing = document.querySelector(`script[src="${MAPKIT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', initMapKit);
      existing.addEventListener('error', () => reject(new Error('mapkit.js failed to load')));
      return;
    }

    const script = document.createElement('script');
    script.src = MAPKIT_SRC;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.addEventListener('load', initMapKit);
    script.addEventListener('error', () => reject(new Error('mapkit.js failed to load')));
    document.head.appendChild(script);
  });

  return mapkitPromise;
}

// ---------------------------------------------------------------------------
// Coordinate / region / zoom helpers
// ---------------------------------------------------------------------------

export function toCoordinate(lat, lng) {
  return new window.mapkit.Coordinate(Number(lat), Number(lng));
}

/**
 * Convert an integer "Google-style" zoom level to a mapkit.CoordinateRegion.
 * Uses Web Mercator math: lngDelta = 360 * widthPx / (256 * 2^zoom).
 */
export function zoomToRegion(center, zoom, widthPx = 1024, heightPx = 768) {
  const lngDelta = (360 * widthPx) / (256 * Math.pow(2, zoom));
  const latRad = (center.lat * Math.PI) / 180;
  const latDelta = lngDelta * (heightPx / widthPx) * Math.cos(latRad);
  return new window.mapkit.CoordinateRegion(
    toCoordinate(center.lat, center.lng),
    new window.mapkit.CoordinateSpan(latDelta, lngDelta)
  );
}

/** Approximate Google-style zoom level from the map's current region. */
export function regionToZoom(map) {
  const widthPx = map.element ? map.element.clientWidth || 1024 : 1024;
  const lngDelta = map.region.span.longitudeDelta || 0.0001;
  const zoom = Math.log2((360 * widthPx) / (256 * lngDelta));
  return Math.max(1, Math.min(21, Math.round(zoom * 100) / 100));
}

/** Extract {ne, sw} bounds (google-map-react shape) from the map's region. */
export function mapToBounds(map) {
  const c = map.region.center;
  const s = map.region.span;
  return {
    ne: { lat: c.latitude + s.latitudeDelta / 2, lng: c.longitude + s.longitudeDelta / 2 },
    sw: { lat: c.latitude - s.latitudeDelta / 2, lng: c.longitude - s.longitudeDelta / 2 },
  };
}

/** Extract plain {lat, lng} center from the map. */
export function mapToCenter(map) {
  return { lat: map.region.center.latitude, lng: map.region.center.longitude };
}

/** Pan the map to a new center, keeping the current span. */
export function panTo(map, lat, lng, animated = true) {
  map.setCenterAnimated(toCoordinate(lat, lng), animated);
}

/** Set a Google-style zoom level, keeping the current center. */
export function setZoom(map, zoom, animated = true) {
  const widthPx = map.element ? map.element.clientWidth || 1024 : 1024;
  const heightPx = map.element ? map.element.clientHeight || 768 : 768;
  const center = mapToCenter(map);
  map.setRegionAnimated(zoomToRegion(center, zoom, widthPx, heightPx), animated);
}

/**
 * True when the map is still attached to a live document. MapKit throws
 * "Cannot read properties of null (reading 'isRooted')" if an annotation is
 * added to a map whose element has been detached (view switch / unmount).
 */
export function isMapUsable(map) {
  if (!map || map.destroyed) return false;
  const el = map.element;
  if (!el) return false;
  try {
    return typeof document !== 'undefined' && document.contains(el);
  } catch (e) {
    return false;
  }
}

/**
 * Add an annotation only when the map can accept it, retrying on the next
 * frame if the element has not been rooted yet. Never throws.
 */
export function safeAddAnnotation(map, annotation, { retries = 3 } = {}) {
  if (!map || !annotation) return false;

  if (!isMapUsable(map)) {
    if (retries > 0 && typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => safeAddAnnotation(map, annotation, { retries: retries - 1 }));
    }
    return false;
  }

  try {
    map.addAnnotation(annotation);
    return true;
  } catch (err) {
    console.warn('[MapKit] addAnnotation skipped:', err?.message || err);
    return false;
  }
}

/**
 * Fit the map region around a set of {lat, lng} points (google's fitBounds).
 * Returns true when the region was applied.
 *
 * @param {object}  map        mapkit.Map instance
 * @param {Array}   points     [{ lat, lng }, …]
 * @param {object}  options    padding (fraction of the span, default 25%),
 *                             minSpan (degrees) and maxZoom for single points
 */
export function fitToPoints(map, points, { padding = 0.25, minSpan = 0.01, animated = true } = {}) {
  if (!map || !Array.isArray(points)) return false;

  const valid = points
    .map((p) => ({ lat: Number(p?.lat), lng: Number(p?.lng) }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng) && (p.lat !== 0 || p.lng !== 0));

  if (valid.length === 0) return false;

  const lats = valid.map((p) => p.lat);
  const lngs = valid.map((p) => p.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);

  const center = { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
  const latDelta = Math.max((maxLat - minLat) * (1 + padding), minSpan);
  const lngDelta = Math.max((maxLng - minLng) * (1 + padding), minSpan);

  try {
    const region = new window.mapkit.CoordinateRegion(
      toCoordinate(center.lat, center.lng),
      new window.mapkit.CoordinateSpan(latDelta, lngDelta)
    );
    if (animated && typeof map.setRegionAnimated === 'function') {
      map.setRegionAnimated(region, true);
    } else {
      map.region = region;
    }
    return true;
  } catch (err) {
    console.warn('[MapKit] fitToPoints failed:', err);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Custom DOM annotations (replacement for google.maps.OverlayView)
// ---------------------------------------------------------------------------

/**
 * Create a custom DOM annotation. `el` is any HTML element you control.
 * anchorOffsetY: negative values raise the element (e.g. pin tips).
 * Returns the mapkit.Annotation. Add/remove via map.addAnnotation/removeAnnotation.
 */
export function createDomAnnotation(position, el, { anchorOffsetY = 0, anchorOffsetX = 0 } = {}) {
  const annotation = new window.mapkit.Annotation(
    toCoordinate(position.lat, position.lng),
    () => el,
    {
      anchorOffset: new DOMPoint(anchorOffsetX, anchorOffsetY),
      // Always keep custom annotations visible; our overlay classes manage
      // their own show/hide via element styles.
      displayPriority: 1000,
      animates: false,
    }
  );
  return annotation;
}

/**
 * Every custom-annotation element we manage. Used to tell an annotation's own
 * wrapper chain apart from the shared layer MapKit renders all annotations in.
 */
const annotationElements = new Set();

export function registerAnnotationElement(el) {
  if (el) annotationElements.add(el);
}

export function unregisterAnnotationElement(el) {
  if (el) annotationElements.delete(el);
}

/**
 * True when `node` also contains a *different* annotation's element, i.e. it is
 * one of MapKit's shared layers rather than this annotation's own wrapper.
 */
function isSharedAnnotationNode(node, el) {
  for (const other of annotationElements) {
    if (other !== el && node.contains(other)) return true;
  }
  return false;
}

/**
 * Resolve the chain of nodes that belong exclusively to a single custom
 * annotation: its own element plus every MapKit wrapper up to (but not
 * including) the shared annotation layer. Those are the nodes whose z-index
 * decides how this annotation stacks against its siblings.
 */
export function getAnnotationStackNodes(el, mapElement = null) {
  const nodes = [];
  let node = el;
  let depth = 0;

  while (node && depth < 6) {
    nodes.push(node);
    const parent = node.parentElement;
    if (!parent || parent === mapElement || parent === document.body) break;
    // Stop once this node sits among sibling annotations (the layer container).
    // Checking for foreign annotation elements is reliable even when MapKit
    // adds extra children (callouts, shadows) to our own wrapper — a plain
    // `children.length > 1` test breaks in that case and leaves the wrapper,
    // the node that actually decides stacking, at MapKit's own z-index.
    if (isSharedAnnotationNode(parent, el)) break;
    node = parent;
    depth += 1;
  }

  return nodes;
}

/**
 * Raise (or lower) a custom annotation above/below its siblings.
 * MapKit wraps the factory element in its own absolutely-positioned
 * containers, so the z-index must be applied to the wrapper that sits
 * among the other annotations, not just to our own element.
 *
 * Returns the nodes that were touched, so callers can keep the value pinned —
 * MapKit rewrites annotation z-indexes on every region change and re-layout.
 */
export function setAnnotationZIndex(el, z, mapElement = null) {
  if (!el) return [];

  const value = String(z);
  const nodes = getAnnotationStackNodes(el, mapElement);

  nodes.forEach((node) => {
    if (node.style.zIndex !== value) node.style.zIndex = value;
  });

  return nodes;
}

/**
 * Mark that an annotation consumed a tap, so the map-level single-tap
 * handler (used for "click background to clear selection") ignores it.
 */
export function markAnnotationTap(map) {
  if (map) map.__lastAnnotationTapAt = Date.now();
}

export function wasAnnotationTapRecent(map, windowMs = 400) {
  return map && map.__lastAnnotationTapAt && Date.now() - map.__lastAnnotationTapAt < windowMs;
}
